import React, { useState, useEffect } from 'react';
import { Star, CheckCircle, XCircle, EyeOff, Filter, Search, MessageSquare, ShieldCheck, Image as ImageIcon } from 'lucide-react';
import type { ReviewStatus } from '@zarin/types';
import { apiFetch } from '../lib/auth';

interface ReviewItem {
  id: string;
  productId: string;
  productTitle: string;
  customerName: string;
  customerEmail: string;
  rating: number;
  title: string;
  body: string;
  fitFeedback: 'runs_small' | 'true_to_size' | 'runs_large';
  sizePurchased?: string;
  photos: string[];
  verifiedBuyer: boolean;
  status: ReviewStatus;
  createdAt: string;
}

const INITIAL_REVIEWS: ReviewItem[] = [];

export default function ReviewsPage() {
  const [reviews, setReviews] = useState<ReviewItem[]>(INITIAL_REVIEWS);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [search, setSearch] = useState<string>('');

  useEffect(() => {
    async function loadReviews() {
      try {
        setLoading(true);
        const res = await apiFetch<{ data: any[] }>('/reviews');
        if (Array.isArray(res.data)) {
          setReviews(res.data.map((r: any) => ({
            id: r.id,
            productId: r.product_id || r.productId,
            productTitle: r.product_title || r.productTitle || 'Garment',
            customerName: r.customer_name || r.customerName || 'Customer',
            customerEmail: r.customer_email || r.customerEmail || '',
            rating: r.rating || 5,
            title: r.title || '',
            body: r.body || '',
            fitFeedback: r.fit_feedback || r.fitFeedback || 'true_to_size',
            sizePurchased: r.size_purchased || r.sizePurchased,
            photos: Array.isArray(r.photos) ? r.photos : (r.photos ? JSON.parse(r.photos) : []),
            verifiedBuyer: Boolean(r.verified_buyer ?? r.verifiedBuyer),
            status: r.status || 'pending',
            createdAt: r.created_at || r.createdAt || new Date().toISOString(),
          })));
        }
      } catch (err) {
        console.error('Failed to load reviews:', err);
      } finally {
        setLoading(false);
      }
    }
    loadReviews();
  }, []);

  const updateStatus = async (id: string, newStatus: ReviewStatus) => {
    setReviews(prev => prev.map(r => r.id === id ? { ...r, status: newStatus } : r));
    try {
      await apiFetch(`/reviews/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });
    } catch (err) {
      console.error('Failed to update review status:', err);
    }
  };

  const filteredReviews = reviews.filter(r => {
    const matchesStatus = filterStatus === 'all' || r.status === filterStatus;
    const matchesSearch =
      r.customerName.toLowerCase().includes(search.toLowerCase()) ||
      r.productTitle.toLowerCase().includes(search.toLowerCase()) ||
      r.body.toLowerCase().includes(search.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const pendingCount = reviews.filter(r => r.status === 'pending').length;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.titleRow}>
            <h1 style={styles.title}>Customer Reviews & Moderation</h1>
            {pendingCount > 0 && (
              <span style={styles.pendingBadge}>{pendingCount} Pending Moderation</span>
            )}
          </div>
          <p style={styles.subtitle}>Review ratings, fit telemetry, verified purchase badges, and photo approvals</p>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div style={styles.toolbar}>
        <div style={styles.searchBox}>
          <Search size={16} color="#888" />
          <input
            type="text"
            placeholder="Search by customer, product, or keyword..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />
        </div>
        <div style={styles.filterGroup}>
          <Filter size={15} color="#888" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={styles.select}
          >
            <option value="all">All Reviews ({reviews.length})</option>
            <option value="pending">Pending ({reviews.filter(r => r.status === 'pending').length})</option>
            <option value="approved">Approved ({reviews.filter(r => r.status === 'approved').length})</option>
            <option value="rejected">Rejected ({reviews.filter(r => r.status === 'rejected').length})</option>
            <option value="hidden">Hidden ({reviews.filter(r => r.status === 'hidden').length})</option>
          </select>
        </div>
      </div>

      {/* Reviews List */}
      <div style={styles.list}>
        {filteredReviews.length === 0 ? (
          <div style={styles.emptyState}>
            <MessageSquare size={36} color="#555" />
            <p style={{ marginTop: 12, color: '#888' }}>No reviews found matching current filter.</p>
          </div>
        ) : (
          filteredReviews.map((rev) => (
            <div key={rev.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <div style={styles.productInfo}>
                  <span style={styles.productTitle}>{rev.productTitle}</span>
                  <span style={styles.date}>{new Date(rev.createdAt).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>
                <div style={styles.ratingStars}>
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      size={15}
                      fill={s <= rev.rating ? '#C9A84C' : 'none'}
                      color={s <= rev.rating ? '#C9A84C' : '#444'}
                    />
                  ))}
                  <span style={styles.statusBadge(rev.status)}>{rev.status.toUpperCase()}</span>
                </div>
              </div>

              <div style={styles.cardBody}>
                <h4 style={styles.reviewTitle}>{rev.title}</h4>
                <p style={styles.reviewText}>{rev.body}</p>

                {rev.photos.length > 0 && (
                  <div style={styles.photoRow}>
                    {rev.photos.map((p, idx) => (
                      <a key={idx} href={p} target="_blank" rel="noreferrer" style={styles.photoThumb}>
                        <img src={p} alt="User review submission" style={styles.thumbImg} />
                      </a>
                    ))}
                  </div>
                )}

                <div style={styles.metaRow}>
                  <div style={styles.customerMeta}>
                    <span style={styles.customerName}>{rev.customerName}</span>
                    {rev.verifiedBuyer && (
                      <span style={styles.verifiedBadge}>
                        <ShieldCheck size={12} color="#10B981" /> Verified Buyer
                      </span>
                    )}
                    {rev.sizePurchased && (
                      <span style={styles.fitTag}>Size: {rev.sizePurchased}</span>
                    )}
                    <span style={styles.fitTag}>Fit: {rev.fitFeedback.replace('_', ' ')}</span>
                  </div>

                  <div style={styles.actions}>
                    {rev.status !== 'approved' && (
                      <button
                        onClick={() => updateStatus(rev.id, 'approved')}
                        style={{ ...styles.actionBtn, ...styles.approveBtn }}
                        title="Approve for storefront"
                      >
                        <CheckCircle size={14} /> Approve
                      </button>
                    )}
                    {rev.status !== 'rejected' && (
                      <button
                        onClick={() => updateStatus(rev.id, 'rejected')}
                        style={{ ...styles.actionBtn, ...styles.rejectBtn }}
                        title="Reject review"
                      >
                        <XCircle size={14} /> Reject
                      </button>
                    )}
                    {rev.status !== 'hidden' && (
                      <button
                        onClick={() => updateStatus(rev.id, 'hidden')}
                        style={{ ...styles.actionBtn, ...styles.hideBtn }}
                        title="Hide without deleting"
                      >
                        <EyeOff size={14} /> Hide
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const styles: Record<string, any> = {
  page: {
    padding: '28px 32px',
    maxWidth: 1200,
    margin: '0 auto',
    color: '#E5E5E5',
  },
  header: {
    marginBottom: 24,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 26,
    fontWeight: 600,
    color: '#FFFFFF',
    margin: 0,
  },
  pendingBadge: {
    background: 'rgba(201, 168, 76, 0.15)',
    color: '#C9A84C',
    border: '1px solid rgba(201, 168, 76, 0.3)',
    borderRadius: 20,
    padding: '3px 10px',
    fontSize: 12,
    fontWeight: 500,
  },
  subtitle: {
    fontSize: 13,
    color: '#8A8A8E',
    marginTop: 6,
  },
  toolbar: {
    display: 'flex',
    gap: 16,
    marginBottom: 20,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: '#141414',
    border: '1px solid #262626',
    borderRadius: 6,
    padding: '8px 14px',
    flex: 1,
  },
  searchInput: {
    background: 'transparent',
    border: 'none',
    color: '#FFF',
    fontSize: 13,
    outline: 'none',
    width: '100%',
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: '#141414',
    border: '1px solid #262626',
    borderRadius: 6,
    padding: '6px 12px',
  },
  select: {
    background: 'transparent',
    border: 'none',
    color: '#FFF',
    fontSize: 13,
    outline: 'none',
    cursor: 'pointer',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    background: '#141414',
    borderRadius: 8,
    border: '1px solid #262626',
  },
  card: {
    background: '#141414',
    border: '1px solid #262626',
    borderRadius: 8,
    padding: 20,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    borderBottom: '1px solid #1F1F1F',
    paddingBottom: 12,
    marginBottom: 12,
  },
  productInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  productTitle: {
    fontWeight: 600,
    fontSize: 14,
    color: '#FFF',
  },
  date: {
    fontSize: 12,
    color: '#71717A',
  },
  ratingStars: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  statusBadge: (status: ReviewStatus) => ({
    marginLeft: 12,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.05em',
    padding: '2px 8px',
    borderRadius: 4,
    background:
      status === 'approved' ? 'rgba(16, 185, 129, 0.15)' :
      status === 'rejected' ? 'rgba(239, 68, 68, 0.15)' :
      status === 'hidden' ? 'rgba(113, 113, 122, 0.15)' : 'rgba(201, 168, 76, 0.15)',
    color:
      status === 'approved' ? '#10B981' :
      status === 'rejected' ? '#EF4444' :
      status === 'hidden' ? '#A1A1AA' : '#C9A84C',
  }),
  cardBody: {},
  reviewTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#E4E4E7',
    margin: '0 0 6px 0',
  },
  reviewText: {
    fontSize: 13,
    lineHeight: 1.6,
    color: '#A1A1AA',
    margin: '0 0 12px 0',
  },
  photoRow: {
    display: 'flex',
    gap: 10,
    marginBottom: 12,
  },
  photoThumb: {
    width: 60,
    height: 60,
    borderRadius: 6,
    overflow: 'hidden',
    border: '1px solid #333',
    display: 'block',
  },
  thumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  metaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 8,
  },
  customerMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    fontSize: 12,
    color: '#8A8A8E',
  },
  customerName: {
    fontWeight: 500,
    color: '#FFF',
  },
  verifiedBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    color: '#10B981',
    fontWeight: 500,
  },
  fitTag: {
    background: '#1F1F1F',
    padding: '2px 8px',
    borderRadius: 4,
    color: '#D4D4D8',
    textTransform: 'capitalize',
  },
  actions: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  actionBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    border: 'none',
    padding: '6px 12px',
    borderRadius: 5,
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'opacity 0.15s ease',
  },
  approveBtn: {
    background: '#10B981',
    color: '#FFF',
  },
  rejectBtn: {
    background: '#EF4444',
    color: '#FFF',
  },
  hideBtn: {
    background: '#27272A',
    color: '#D4D4D8',
  },
};
